import normalize from 'jsonapi-normalizer'
import axios from 'axios'
import * as _ from 'lodash'
import * as config from 'config'
import { getToken } from './JwtTokenUtil'
import * as R from 'ramda'
import store from 'configureStore'
import {
  postLogoutUser,
} from 'containers/User/Login/actions'

const ERRORS = Object.freeze({
  'Unauthorized': 401
})

const formatRequest = (data) => keysToSnakeCase(data)
const formatResponse = ({data}) => keysToCamelCase(data)

const keysToCamelCase = (object) => {
  let camelCaseObject = _.cloneDeep(object)

  if (_.isArray(camelCaseObject)) {
    return _.map(camelCaseObject, keysToCamelCase)
  } else if (_.isPlainObject(camelCaseObject)) {
    camelCaseObject = _.mapKeys(camelCaseObject, (value, key) => {
      return _.camelCase(key)
    })

    // Recursively apply throughout object
    return _.mapValues(camelCaseObject, (value) => {
      if (_.isPlainObject(value)) {
        return keysToCamelCase(value)
      } else if (_.isArray(value)) {
        return _.map(value, keysToCamelCase)
      } else {
        return value
      }
    })
  } else {
    return camelCaseObject
  }
}

const keysToSnakeCase = (object) => {
  let snakeCaseObject = _.cloneDeep(object)

  if (_.isArray(snakeCaseObject)) {
    return _.map(snakeCaseObject, keysToSnakeCase)
  } else if(_.isPlainObject(snakeCaseObject)) {
    snakeCaseObject = _.mapKeys(snakeCaseObject, (value, key) => {
      return _.snakeCase(key)
    })

    // Recursively apply throughout object
    return _.mapValues(snakeCaseObject, (value) => {
      if (_.isPlainObject(value)) {
        return keysToSnakeCase(value)
      } else if (_.isArray(value)) {
        return _.map(value, keysToSnakeCase)
      } else {
        return value
      }
    })
  } else {
    return snakeCaseObject
  }
}

const baseRequest = (callback) => {
  return (url) => {
    return callback(url,jwtConfig())
      .then(response => formatResponse(response))
      .catch(error => {
        if (error.response.status === ERRORS.Unauthorized) {
          store.dispatch(postLogoutUser())
        }
        return Promise.reject(formatResponse(error.response).errors[0])
      })
  }
}


const baseDataRequest = (callback) => {
  return (url, data) => {
    return callback(
      url,
      formatRequest(data),
      jwtConfig()
    )
    .then(response => formatResponse(response))
    .catch(error => {
      if (error.response.status === ERRORS.Unauthorized) {
        store.dispatch(postLogoutUser())
      }
      return Promise.reject(formatResponse(error.response).errors[0])
    })
  }
}

const defaultConfig = (headers) => {
  return {
    headers: { ...headers },
    baseURL: config.baseURL
  }
}

const jsonapiConfig = (headers) => {
  return defaultConfig({
    'Content-Type': 'application/vnd.api+json',
    'Accept': 'application/vnd.api+json',
    ...headers
  })
}

const jwtConfig = (headers) => {
  return jsonapiConfig({
    'Authorization': `Bearer ${getToken()}`,
    ...headers
  })
}

const loginConfig = () => ({
  baseURL: config.loginURL,
  headers: {
    'Content-Type': 'application/vnd.api+json',
    'Accept': 'application/vnd.api+json',
  }
})

const formDataHeader = () => {
  return jsonapiConfig({
    'Authorization': `Bearer ${getToken()}`,
    'Content-Type': 'multipart/form-data'
  })
}

const buildFormData = (obj) => {
  const formData = new FormData()
  Object.entries(obj).forEach(([key, value]) => formData.append(key, value))
  return formData
}

const postAttributeFormatter = (type, attributes) => {
  return {
    data: {
      type,
      attributes
    }
  }
}

const getAttributeFormatter = (type, attributes) => {
  return {
    data: {
      type,
      attributes
    }
  }
}

const patchAttributeFormatter = (id, type, attributes) => {
  return {
    data: {
      id,
      type,
      attributes
    }
  }
}

// Non normalized
const getRequest = (url) => baseRequest(axios.get)(url)
const postRequest = R.curry((type, attributes, url) => {
  const data = getAttributeFormatter(type, attributes)
  return baseDataRequest(axios.post)(url, data)
})


// Normalized
const del = (url) => baseRequest(axios.delete)(url)
const get = (url) => getRequest(url).then(data => {
  return normalize(data)
})
const getWithMeta = (url) => getRequest(url).then(data => ({
  data: normalize(data),
  meta: data.meta
}))

const post = R.curry((type, attributes, url, toNormalize = true) => {
  return postRequest(type, attributes, url)
    .then(data => {
      return toNormalize ? normalize(data) : data
    })
})

const patch = R.curry((id, type, attributes, url) => {
  const data = patchAttributeFormatter(id, type, attributes)
  const path = `${url}/${id}`
  return baseDataRequest(axios.patch)(path, data)
    .then(data => normalize(data))
})

const normalizer = (data, type, propertyId) => {
  const dataFormatter = R.compose(
    normalize,
    (data) => ({ data }),
    R.map((value) => {
      let id = R.prop(propertyId)(value)
      return {
        id,
        type,
        attributes: { ...value }
      }
    })
  )
  return dataFormatter(data)
}

// const postReq = (url, data, shouldFormat = true) => baseDataRequest(axios.post, data, shouldFormat)(url)


const withAuthorization = (callback) => {
  return (url) => {
    return callback(url,jwtConfig())
      .catch(error => {
        if (error.response.status === ERRORS.Unauthorized) {
          store.dispatch(postLogoutUser())
        }
        return Promise.reject(formatResponse(error.response))
      })
  }
}

const getEntityData = (data, entityName) => {
  if(data.entities[entityName] && Object.values(data.entities[entityName])[0]){
    return Object.values(data.entities[entityName])[0]
  } else {
    return {}
  }
}


export {
  getRequest,
  postRequest,
  get,
  getWithMeta,
  patch,
  post,
  del,
  postAttributeFormatter,
  getAttributeFormatter,
  patchAttributeFormatter,
  formatRequest,
  formatResponse,
  jsonapiConfig,
  loginConfig,
  jwtConfig,
  formDataHeader,
  buildFormData,
  keysToCamelCase,
  normalizer,
  withAuthorization,
  getEntityData
}
